import { ActionIconComposition, TextInputComposition, DrawerComposition } from '@codeleap/web'
import { createStyles } from '@codeleap/styles'
import { StyleRegistry } from '../styles'
import { textVariant } from './Text'
import { AvatarInfoComposition } from './AvatarInfo'
import { NavigationMenuComposition } from './NavigationMenu'

type ActionIconParts =
  | `backActionIcon${Capitalize<ActionIconComposition>}`
  | `contentActionIcon${Capitalize<ActionIconComposition>}`

type SearchInputParts =
  | `searchInput${Capitalize<TextInputComposition>}`

type AvatarInfoParts =
  | `avatarInfo${Capitalize<AvatarInfoComposition>}`

export type DrawerParts =
  | `drawer${Capitalize<DrawerComposition>}`
  | `menuActionIcon${Capitalize<ActionIconComposition>}`
  | `navigationMenu${Capitalize<NavigationMenuComposition>}`

export type PageHeaderParts =
  | 'wrapper'
  | 'innerWrapper'
  | 'contentWrapper'
  | 'infoWrapper'
  | 'actionWrapper'

export type PageHeaderComposition = PageHeaderParts | ActionIconParts | SearchInputParts | AvatarInfoParts | DrawerParts

const createPageHeaderVariant = createStyles<PageHeaderComposition>

export const PageHeaderStyles = {
  default: createPageHeaderVariant((theme) => {
    const avatarSize = theme.size.largeish

    return {
      wrapper: {
        ...theme.presets.fullWidth,
        ...theme.presets.column,
        ...theme.presets.alignStart,
        ...theme.presets.justifyStart,
        ...theme.spacing.padding(2),
        ...theme.spacing.gap(2),
      },
      innerWrapper: {
        ...theme.presets.fullWidth,
        ...theme.presets.alignCenter,
        ...theme.presets.justifySpaceBetween,
      },
      contentWrapper: {
        ...theme.presets.flex,
        ...theme.presets.column,
        ...theme.spacing.gap(2),
        ...theme.presets.fullWidth,
      },
      infoWrapper: {
        ...theme.presets.flex,
        ...theme.presets.row,
        ...theme.spacing.gap(1),
        ...theme.presets.fullWidth,
      },
      actionWrapper: {
        ...theme.presets.row,
        ...theme.presets.alignCenter,
        ...theme.spacing.gap(1),
        ...theme.presets.fullWidth,
      },
      // Back ActionIcon
      backActionIconTouchableWrapper: {
        width: theme.size.largeish,
        height: theme.size.largeish,
        backgroundColor: theme.colors.buttonNeutralQuaternaryBgDefault,
        ...theme.border({ color: theme.colors.buttonNeutralQuaternaryBgDefault, width: 0 }),
      },
      backActionIconIcon: {
        width: theme.values.iconSize[1],
        height: theme.values.iconSize[1],
        color: theme.colors.buttonHighlightQuaternaryIconDefault,
      },

      // Menu ActionIcon
      menuActionIconTouchableWrapper: {
        width: theme.size.largeish,
        height: theme.size.largeish,
        backgroundColor: theme.colors.buttonHighlightPrimaryBgDefault,
        ...theme.border({ color: theme.baseColors.primarySolid500, width: 0 }),
      },
      menuActionIconIcon: {
        width: theme.values.iconSize[2],
        height: theme.values.iconSize[2],
        color: theme.baseColors.primarySolid500,
      },

      // ContentActionIcon
      contentActionIconTouchableWrapper: {
        width: theme.size.largeish,
        height: theme.size.largeish,
        backgroundColor: theme.baseColors.primarySolid50,
        ...theme.border({
          color: theme.baseColors.primarySolid50,
          width: theme.stroke.regular,
        }),
      },
      contentActionIconIcon: {
        width: theme.values.iconSize[2],
        height: theme.values.iconSize[2],
        color: theme.colors.buttonHighlightSecondaryIconDefault,
      },

      //AvatarInfo
      avatarInfoWrapper: {
        ...theme.spacing.gap(1),
      },
      avatarInfoAvatarWrapper: {
        width: avatarSize,
        height: avatarSize,
        minWidth: avatarSize,
        maxWidth: avatarSize,
        minHeight: avatarSize,
        maxHeight: avatarSize,
      },
      avatarInfoAvatarImage: {
        height: avatarSize,
        width: avatarSize,
      },
      avatarInfoAvatarInitials: {
        ...textVariant('c1'),
        color: theme.baseColors.primarySolid50,
        fontWeight: 700,
      },
      avatarInfoContentWrapper: {
        ...theme.spacing.gap(0),
      },
      avatarInfoTitle: {
        ...textVariant('p2'),
        color: theme.baseColors.onPrimarySolid500Heading,
        fontWeight: 700,
      },
      avatarInfoDescription: {
        ...textVariant('c2'),
        color: theme.baseColors.neutralBlackTransparent700,
      },
    }
  }),
}

StyleRegistry.registerVariants('PageHeader', PageHeaderStyles)
